import React, { useState, useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
  useLocation,
  useNavigate,
  Link,
  Navigate,
} from "react-router-dom";
import { WorkspaceProvider } from "./context/WorkspaceContext";
import { StrategyInsightsProvider } from "./context/StrategyInsightsContext";
import StrategyDashboardSprout from "./components/layout/StrategyDashboardSprout";
import { BusinessBrainProvider } from "./context/BusinessBrainContext";
import { ThemeProvider } from "./context/ThemeContext";
import { GnomeProvider, useGnomeContext } from "./context/GnomeContext";
import { ProfileProvider } from "./context/ProfileContext";
import { GenerationProvider } from "./context/GenerationContext";
// import Sidebar from './components/layout/Sidebar'; // Deprecated/Removed
import Header from "./components/layout/Header";
import GnomeChatModal from "./components/gnomes/GnomeChatModal";
import Logo from "./components/ui/Logo";
import DownloadAppButton from "./components/common/DownloadAppButton";
import GlobalGenerationOverlay from "./components/common/GlobalGenerationOverlay";
const Dashboard = lazy(() => import("./pages/Dashboard"));
const StrategyHub = lazy(() => import("./pages/StrategyHub"));
const WorkspaceDetail = lazy(() => import("./pages/WorkspaceDetail"));
const Tasks = lazy(() => import("./pages/Tasks"));
const AdCreatives = lazy(() => import("./pages/AdCreatives"));
const TeamMeeting = lazy(() => import("./pages/TeamMeeting"));
const ClientMeeting = lazy(() => import("./pages/ClientMeeting"));
const CommunityInbox = lazy(() => import("./pages/CommunityInbox"));
const Campaigns = lazy(() => import("./pages/Campaigns"));
const EmailAutomation = lazy(() => import("./pages/EmailAutomation"));
const Placements = lazy(() => import("./pages/Placements"));
const ContentAutomation = lazy(() => import("./pages/ContentAutomation"));
const Engagement = lazy(() => import("./pages/Engagement"));
const Audience = lazy(() => import("./pages/Audience"));

const PerformanceReviews = lazy(() => import("./pages/PerformanceReviews"));
const Achievements = lazy(() => import("./pages/Achievements"));
const QuickScore = lazy(() => import("./pages/QuickScore"));
const Social = lazy(() => import("./pages/Social"));
const Seo = lazy(() => import("./pages/Seo"));
const Technology = lazy(() => import("./pages/Technology"));
const Settings = lazy(() => import("./pages/Settings"));
const Billing = lazy(() => import("./pages/Billing"));
const ConnectApps = lazy(() => import("./pages/ConnectApps"));
const Help = lazy(() => import("./pages/Help"));
const FAQ = lazy(() => import("./pages/FAQ"));
const MarketingTerminology = lazy(() => import("./pages/MarketingTerminology"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const DataRights = lazy(() => import("./pages/DataRights"));
const Terms = lazy(() => import("./pages/Terms"));
const About = lazy(() => import("./pages/About"));
const Pricing = lazy(() => import("./pages/Pricing"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const PerformanceAnalytics = lazy(() => import("./pages/PerformanceAnalytics"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AuthConfirm = lazy(() => import("./pages/AuthConfirm"));
const InvitedSetup = lazy(() => import("./pages/InvitedSetup"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const SevenHatsCampaign = lazy(() => import("./pages/SevenHatsCampaign"));
const Careers = lazy(() => import("./pages/Careers"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const Roadmap = lazy(() => import("./pages/Roadmap"));
const Branding = lazy(() => import("./pages/Branding"));
const BrandingSettings = lazy(() => import("./pages/BrandingSettings"));
const MarketingAgencies = lazy(() => import("./pages/use-cases/MarketingAgencies"));
const Consultants = lazy(() => import("./pages/use-cases/Consultants"));
const FractionalCMOs = lazy(() => import("./pages/use-cases/FractionalCMOs"));
const Integrations = lazy(() => import("./pages/Integrations"));
const IntegrationDetail = lazy(() => import("./pages/IntegrationDetail"));
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const CaseStudyDetail = lazy(() => import("./pages/CaseStudyDetail"));
const StrategyExamples = lazy(() => import("./pages/StrategyExamples"));
const StrategyExampleDetail = lazy(() => import("./pages/StrategyExampleDetail"));
const GnomeDetail = lazy(() => import("./pages/GnomeDetail"));
const PublicPage = lazy(() => import("./pages/PublicPage"));
const AdLibrary = lazy(() => import("./pages/AdLibrary"));
const NotifyMe = lazy(() => import("./pages/NotifyMe"));
import ProtectedRoute from "./components/auth/ProtectedRoute";
import AdminRoute from "./components/auth/AdminRoute";
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ExpressDashboard = lazy(() => import("./pages/ExpressDashboard"));
import PublicLayout from "./layouts/PublicLayout";
const MaintenancePage = lazy(() => import("./pages/MaintenancePage"));
const BuildBrilliantly = lazy(() => import("./pages/BuildBrilliantly"));
const InstantPageRenderer = lazy(() => import("./pages/InstantPageRenderer"));
import WorkspaceSelector from "./components/layout/WorkspaceSelector";
import { Toaster } from "sonner";
import { supabase } from "./supabase";
import { useSupabaseAuth } from "./hooks/useSupabaseAuth";
import AuthRedirect from "./components/auth/AuthRedirect";
import VisitorTracker from "./components/common/VisitorTracker";
import IntentTracker from "./components/analytics/IntentTracker";
import TrialExpiredModal from "./components/common/TrialExpiredModal";
import TrialBanner from "./components/common/TrialBanner";
import LoadingPage from "./components/common/LoadingPage";
import FunnelProxy from "./components/common/FunnelProxy";
import { useWorkspace } from "./context/WorkspaceContext";
import ErrorBoundary from "./components/common/ErrorBoundary";
import ScrollToTop from "./components/ScrollToTop";
import "./App.css";
import { DOMAINS, getMarketingUrl } from "./config/domains";
import { isNativePlatform } from "./utils/nativeUtils";
import MobileLayout from "./components/mobile/MobileLayout";
const MobileDashboard = lazy(
  () => import("./components/mobile/MobileDashboard"),
);
import MobileChat from "./components/mobile/MobileChat";
import MobileTaskBoard from "./components/mobile/MobileTaskBoard";
import MobileQuickActions from "./components/mobile/MobileQuickActions";
import MobileCalendar from "./components/mobile/MobileCalendar";
import { MobileAuthProvider } from "./context/MobileAuthContext";

// --- Domain Configuration ---

const isMarketingDomain = window.location.hostname === DOMAINS.MARKETING;
const isAppDomain = window.location.hostname === DOMAINS.APP;
const isProduction = isMarketingDomain || isAppDomain;

const RootRedirect = () => {
  const location = useLocation();


  const lastPath = localStorage.getItem("last_visited_path");
  const target = lastPath && lastPath !== "/" ? lastPath : "/dashboard";
  return (
    <Navigate to={{ pathname: target, search: location.search }} replace />
  );
};

// Define known paths for smart redirection
const PUBLIC_PATHS = [
  "/",
  "/about",
  "/careers",
  "/roadmap",
  "/contact",
  "/privacy",
  "/data-rights",
  "/terms",
  "/diagnostics",
  "/marketing-agencies",
  "/consultants",
  "/fractional-cmos",
  "/integrations",
  "/case-studies",
  "/strategy-examples",
  "/academy",
  "/workshops",
  "/blueprints",
  "/partners",
  "/experts",
  "/affiliate",
  "/developers",
  "/community",
  "/status",
  "/help",
  "/faq",
  "/notify-me",
  "/seven-hats",
  "/ad-library",
];

const isPublicRoute = (pathname) => {
  return (
    PUBLIC_PATHS.includes(pathname) ||
    pathname.startsWith("/blog") ||
    pathname.startsWith("/case-studies") ||
    pathname.startsWith("/strategy-examples") ||
    pathname.startsWith("/integrations") ||
    pathname.startsWith("/p/") ||
    pathname.startsWith("/gnomes/")
  );
};

function PathTracker() {
  const location = useLocation();

  useEffect(() => {
    // Determine if the current path is one we want to save
    const authPaths = ["/login", "/signup"];
    const isAuthPath = authPaths.includes(location.pathname);
    const isPublicPath = isPublicRoute(location.pathname);

    // Don't save if it's a public route, auth route, or the exact root
    if (!isAuthPath && !isPublicPath && location.pathname !== "/") {
      localStorage.setItem(
        "last_visited_path",
        location.pathname + location.search + location.hash,
      );
    }
  }, [location]);

  return null;
}

function MaintenanceCheck({ children }) {
  const [maintenanceSettings, setMaintenanceSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const { user, loading: authLoading } = useSupabaseAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkedUserId, setCheckedUserId] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const checkMaintenance = async () => {
      let cachedUsed = false;
      let shouldFetch = true;
      const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

      try {
        const cached = localStorage.getItem("maintenance_settings_v1");
        if (cached && cached !== "undefined" && cached !== "null" && cached !== "[object Object]") {
          const parsed = JSON.parse(cached);
          if (parsed && typeof parsed === 'object') {
            const { data, timestamp } = parsed;
            if (data) {
              setMaintenanceSettings(data);
              setSettingsLoading(false);
              cachedUsed = true;

              // If we have a valid timestamp and it's within TTL, skip fetch
              if (timestamp && Date.now() - timestamp < CACHE_TTL) {
                shouldFetch = false;
              }
            }
          }
        }
      } catch (e) {
        // Silently catch and proceed to fetch
      }

      if (!shouldFetch) {
        return;
      }

      // If we don't need to block initial load (because we used cache),
      // we can do a background revalidation. If we DO need to block
      // (no cache), we await the fetch and then unblock.

      const fetchMaintenanceSettings = async () => {
        try {
          const { data, error } = await supabase
            .from("platform_settings")
            .select(
              "maintenance_mode_public, maintenance_mode_app, maintenance_message, maintenance_duration",
            )
            .single();

          if (error) throw error;

          setMaintenanceSettings(data);
          localStorage.setItem(
            "maintenance_settings_v1",
            JSON.stringify({
              data,
              timestamp: Date.now(),
            }),
          );
        } catch (e) {
          console.error("Error checking maintenance mode", e);
        } finally {
          if (!cachedUsed) {
            setSettingsLoading(false);
          }
        }
      };

      if (cachedUsed) {
        // We have cache, UI is already unblocked, fetch in background
        fetchMaintenanceSettings();
      } else {
        // No cache, we MUST block UI until we know if we're in maintenance mode
        await fetchMaintenanceSettings();
      }
    };
    checkMaintenance();
  }, []);

  useEffect(() => {
    const checkAdmin = async () => {
      if (!user?.id) {
        setIsAdmin(false);
        setCheckedUserId(null);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", user.id)
          .maybeSingle();

        if (error && error.code !== "42P01") {
          console.error("Error checking admin status", error);
        }

        if (data?.role === "super_admin" || data?.role === "admin") {
          setIsAdmin(true);
        } else {
          setIsAdmin(false);
        }
      } catch (e) {
        console.error("Error checking admin status", e);
        setIsAdmin(false);
      } finally {
        setCheckedUserId(user.id);
      }
    };

    checkAdmin();
  }, [user?.id]); // Use user?.id instead of user to prevent unnecessary re-fetches

  // Define paths
  const authPaths = ["/login", "/signup"];

  const isPublicPath = PUBLIC_PATHS.some(
    (path) =>
      location.pathname === path ||
      location.pathname.startsWith("/blog") ||
      location.pathname.startsWith("/case-studies") ||
      location.pathname.startsWith("/strategy-examples") ||
      location.pathname.startsWith("/integrations") ||
      location.pathname.startsWith("/p/"),
  );
  const isAuthPath = authPaths.includes(location.pathname);

  // App paths are everything else that isn't public and isn't auth?
  const isAppPath = !isPublicPath && !isAuthPath;

  const isCheckingAdmin = user && user.id !== checkedUserId;

  if (settingsLoading || authLoading || isCheckingAdmin) {
    return <LoadingPage message="Loading application..." />;
  }

  // Admin Bypass
  if (isAdmin && user) {
    return children;
  }

  // Public Site Maintenance Logic
  if (maintenanceSettings?.maintenance_mode_public) {
    if (isPublicPath) {
      return <MaintenancePage settings={maintenanceSettings} />;
    }
  }

  // Application Maintenance Logic
  if (maintenanceSettings?.maintenance_mode_app) {
    if (isAppPath) {
      return <MaintenancePage settings={maintenanceSettings} />;
    }
  }

  return children;
}

const StickyFooter = () => (
  <footer className="mt-auto py-4 border-t border-charcoal-100 dark:border-charcoal-800 bg-white dark:bg-charcoal-900 px-6">
    <div className="grid grid-cols-1 md:grid-cols-3 items-center w-full gap-4 text-xs text-charcoal-500 dark:text-charcoal-400">
      <div className="flex gap-4 justify-center md:justify-start items-center">
        <Link
          to="/terms"
          className="hover:text-charcoal-900 dark:hover:text-charcoal-200"
        >
          Terms
        </Link>
        <Link
          to="/privacy"
          className="hover:text-charcoal-900 dark:hover:text-charcoal-200"
        >
          Privacy Policy
        </Link>
        <Link
          to="/contact"
          className="hover:text-charcoal-900 dark:hover:text-charcoal-200"
        >
          Contact Us
        </Link>
        <Link
          to="/help"
          className="hover:text-charcoal-900 dark:hover:text-charcoal-200"
        >
          Help Center
        </Link>
        <DownloadAppButton className="hover:text-charcoal-900 dark:hover:text-charcoal-200" />
        <span>&copy; {new Date().getFullYear()}</span>
      </div>
      <div className="flex flex-col items-center justify-center gap-1">
        <a
          href={getMarketingUrl()}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:opacity-80 transition-opacity flex items-center"
        >
          <span className="text-lg font-bold text-charcoal-900 dark:text-white mr-1">
            🍄
          </span>
          <Logo className="h-10" textOnly={true} />
        </a>
      </div>
      <div className="hidden md:block"></div>
    </div>
  </footer>
);

function Layout() {
  const { isTrialExpired, viewMode, activeWorkspace, userRole } = useWorkspace();
  const { chatModalOpen, selectedGnome, closeChat } = useGnomeContext();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (viewMode === "express" && location.pathname === "/dashboard") {
      navigate("/express");
    } else if (viewMode === "pro" && location.pathname === "/express") {
      navigate("/dashboard");
    }
  }, [viewMode, location.pathname, navigate]);

  return (
    // Replaced bg-app-bg with bg-black (charcoal)
    <div className="flex h-screen bg-black overflow-hidden print:overflow-visible print:h-auto print:block p-0 md:p-6 relative">
      <a
        href="#app-main-scroll-container"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 z-[9999] px-4 py-2 bg-white dark:bg-charcoal-800 text-charcoal-900 dark:text-white rounded-md shadow-lg font-medium border border-charcoal-200 dark:border-charcoal-700 transition-all"
      >
        Skip to content
      </a>
      {isTrialExpired && <TrialExpiredModal />}
      <Toaster position="top-right" />

      {/* Trial Banner */}
      <div className="absolute top-0 left-0 right-0 z-50 md:hidden">
        <TrialBanner />
      </div>

      {/* Main App Card - "Window in Window" */}
      <div className="flex-1 flex flex-col bg-white dark:bg-charcoal-900 md:rounded-[2rem] shadow-2xl overflow-hidden print:overflow-visible print:block relative border border-charcoal-100 dark:border-charcoal-800">
        <div className="hidden md:block">
          <TrialBanner />
        </div>
        {!isTrialExpired && <Header />}

        {/* Main Content Scroll Area */}
        <main
          id="app-main-scroll-container"
          className="flex-1 overflow-y-auto bg-charcoal-50 dark:bg-charcoal-950 flex flex-col relative"
        >
          <div className="flex-1 p-4 md:p-6 pb-20 md:pb-6">
            <Outlet key={activeWorkspace?.id} />
          </div>
        </main>

        {/* Sticky Footer fixed at bottom of card */}
        <StickyFooter />
      </div>

      {/* Global Gnome Assistant */}


      {/* Global Gnome Chat Modal */}
      {!isTrialExpired && userRole === 'super_admin' && (
        <GnomeChatModal
          isOpen={chatModalOpen}
          onClose={closeChat}
          gnome={selectedGnome}
        />
      )}
    </div>
  );
}

const CatchAllRoute = () => {
  const location = useLocation();

  // Determine if we should redirect based on current location and domain
  // Rerouting between App and Marketing subdomains has been removed.
  const shouldRedirectMarketingToApp = false;
  const shouldRedirectAppToMarketing = false;

  useEffect(() => {
    // Persist mode=native in session storage if detected
    if (location.search.includes("mode=native")) {
      sessionStorage.setItem("isNativeMode", "true");
    }
  }, [location.search]);

  useEffect(() => {
    // Only perform redirects if we are in production
    if (isProduction) {
      // 1. On Marketing Domain (sevenhats.com)
      if (shouldRedirectMarketingToApp) {
        window.location.href = `https://${DOMAINS.APP}${location.pathname}${location.search}${location.hash}`;
      }
      // 2. On App Domain (app.sevenhats.com)
      else if (shouldRedirectAppToMarketing) {
        window.location.href = `https://${DOMAINS.MARKETING}${location.pathname}${location.search}${location.hash}`;
      }
    }
  }, [location, shouldRedirectMarketingToApp, shouldRedirectAppToMarketing]);

  // Google Tag Manager Page View Tracking
  useEffect(() => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "page_view",
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);

  // Handle Supabase OAuth redirect fragments that React Router sees as paths
  if (
    location.pathname.startsWith("/access_token") ||
    location.pathname.startsWith("/error_description") ||
    location.pathname.includes("access_token=")
  ) {
    return (
      <div className="flex h-screen items-center justify-center bg-charcoal-50 dark:bg-charcoal-950">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-charcoal-900 dark:border-charcoal-100"></div>
        <span className="ml-3 text-charcoal-600 dark:text-charcoal-400">
          Completing Sign In...
        </span>
      </div>
    );
  }

  // Show loading state if we are about to redirect
  if (
    isProduction &&
    (shouldRedirectMarketingToApp || shouldRedirectAppToMarketing)
  ) {
    return (
      <div className="flex h-screen items-center justify-center bg-charcoal-50 dark:bg-charcoal-950">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-charcoal-900 dark:border-charcoal-100"></div>
        <span className="ml-3 text-charcoal-600 dark:text-charcoal-400">
          Redirecting...
        </span>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center bg-charcoal-50 dark:bg-charcoal-950 text-charcoal-900 dark:text-white">
      <div className="text-center">
        <h1 className="text-4xl font-bold mb-4">404</h1>
        <p className="mb-4">Page not found</p>
        {/*
              Context-aware "Go Home" button
              If on app, go to dashboard. If on marketing, go to home.
            */}
        {isAppDomain ? (
          <Link to="/dashboard" className="text-blue-600 hover:underline">
            Go to Dashboard
          </Link>
        ) : (
          <Link to="/" className="text-blue-600 hover:underline">
            Go Home
          </Link>
        )}
      </div>
    </div>
  );
};

// --- Route Definitions ---

const PublicRoutes = () => (
  <Route element={<PublicLayout />}>
    <Route path="/" element={<LandingPage />} />
    <Route path="/build-brilliantly" element={<BuildBrilliantly />} />
    <Route path="/seven-hats" element={<SevenHatsCampaign />} />
    <Route path="/about" element={<About />} />
    <Route path="/pricing" element={<Pricing />} />
    <Route path="/how-it-works" element={<HowItWorks />} />
    <Route path="/performance-analytics" element={<PerformanceAnalytics />} />
    <Route path="/careers" element={<Careers />} />
    <Route path="/blog" element={<Blog />} />
    <Route path="/blog/:id" element={<BlogDetail />} />
    <Route path="/roadmap" element={<Roadmap />} />
    <Route path="/contact" element={<Contact />} />
    <Route path="/privacy" element={<Privacy />} />
    <Route path="/data-rights" element={<DataRights />} />
    <Route path="/terms" element={<Terms />} />
    <Route path="/marketing-agencies" element={<MarketingAgencies />} />
    <Route path="/consultants" element={<Consultants />} />
    <Route path="/fractional-cmos" element={<FractionalCMOs />} />
    <Route path="/integrations" element={<Integrations />} />
    <Route path="/integrations/:platformId" element={<IntegrationDetail />} />
    <Route path="/case-studies" element={<CaseStudies />} />
    <Route path="/case-studies/:id" element={<CaseStudyDetail />} />
        <Route path="/strategy-examples" element={<StrategyExamples />} />
        <Route path="/strategy-examples/:id" element={<StrategyExampleDetail />} />
    <Route path="/gnomes/:slug" element={<GnomeDetail />} />

    {/* New Mega Menu Pages */}
    <Route path="/academy" element={<PublicPage pageId="academy" />} />
    <Route path="/workshops" element={<PublicPage pageId="workshops" />} />
    <Route path="/blueprints" element={<PublicPage pageId="blueprints" />} />
    <Route path="/partners" element={<PublicPage pageId="partners" />} />
    <Route path="/experts" element={<PublicPage pageId="experts" />} />
    <Route path="/affiliate" element={<PublicPage pageId="affiliate" />} />
    <Route path="/community" element={<PublicPage pageId="community" />} />
    <Route path="/status" element={<PublicPage pageId="status" />} />
    <Route path="/help" element={<Help />} />
    <Route path="/faq" element={<FAQ />} />
    <Route path="/marketing-terminology" element={<MarketingTerminology />} />
    <Route path="/notify-me" element={<NotifyMe />} />
    <Route path="/p/:slug" element={<InstantPageRenderer />} />
    <Route path="/ad-library" element={<AdLibrary />} />
  </Route>
);

const AppRoutes = () => (
  <>
    <Route path="/login" element={<Login />} />
    <Route path="/signup" element={<Signup />} />
    <Route path="/forgot-password" element={<ForgotPassword />} />
    <Route path="/reset-password" element={<ResetPassword />} />
    <Route path="/auth/confirm" element={<AuthConfirm />} />
    <Route path="/invited-setup" element={<InvitedSetup />} />
    <Route element={<ProtectedRoute />}>
      <Route element={<Layout />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/workspaces/:id" element={<WorkspaceDetail />} />
        <Route path="/express" element={<ExpressDashboard />} />
        <Route path="/seo" element={<Seo />} />

        <Route path="/performance-reviews" element={<PerformanceReviews />} />
        <Route path="/strategy" element={<StrategyHub />} />
        <Route path="/technology" element={<Technology />} />
        <Route path="/quick-score" element={<QuickScore />} />
        <Route path="/branding" element={<Branding />} />
        <Route path="/achievements" element={<Achievements />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/settings/branding" element={<BrandingSettings />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/connect-apps" element={<ConnectApps />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/help" element={<Help />} />

        {/* Admin Routes */}
        <Route element={<AdminRoute />}>
          <Route path="/team-meeting" element={<TeamMeeting />} />
          <Route path="/client-meeting" element={<ClientMeeting />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/tasks" element={<Tasks />} />
          <Route path="/creatives" element={<AdCreatives />} />
          <Route path="/engagement" element={<Engagement />} />
          <Route path="/audience" element={<Audience />} />
          <Route path="/email-automation" element={<EmailAutomation />} />
          <Route path="/campaigns" element={<Campaigns />} />
          <Route path="/analytics" element={<Navigate to="/performance-reviews" replace />} />
          <Route path="/community-inbox" element={<CommunityInbox />} />
          <Route path="/content" element={<ContentAutomation />} />
          <Route path="/social" element={<Social />} />
        </Route>
      </Route>
    </Route>
  </>
);

// --- Mobile Routes ---
const MobileRoutes = () => (
  <Route element={<ProtectedRoute />}>
    <Route element={<MobileLayout />}>
      <Route path="/mobile" element={<MobileDashboard />} />
      <Route path="/mobile/chat" element={<MobileChat />} />
      <Route path="/mobile/tasks" element={<MobileTaskBoard />} />
      <Route path="/mobile/calendar" element={<MobileCalendar />} />
      <Route path="/mobile/actions" element={<MobileQuickActions />} />
      {/* Redirect root mobile to dashboard */}
      <Route path="/" element={<Navigate to="/mobile" replace />} />
    </Route>
  </Route>
);

function App() {
  const isNative = isNativePlatform();

  return (
    <ErrorBoundary>
      <Router>
        <WorkspaceProvider>
      <StrategyInsightsProvider>
          <ProfileProvider>
            <BusinessBrainProvider>
              <GnomeProvider>
                <GenerationProvider>
                  <ThemeProvider>
                    <MobileAuthProvider>
                      <ScrollToTop />
                      <GlobalGenerationOverlay />
                      <PathTracker />
                      <MaintenanceCheck>
                        <AuthRedirect />
                        <VisitorTracker />
                        <IntentTracker />
                        <StrategyDashboardSprout />
                        <Suspense fallback={<LoadingPage />}>
                          <Routes>
                            {/*
                                NATIVE MOBILE ROUTING STRATEGY
                                ------------------------------
                                If running natively or in ?mode=native:
                                - Render MobileRoutes at root
                                - Fallback to public/web routes if needed?
                                  Actually, native app should strictly stay in mobile view.
                            */}
                            {isNative && MobileRoutes()}
                            {isNative && (
                              <Route
                                path="*"
                                element={<Navigate to="/mobile" replace />}
                              />
                            )}

                            {/*
                                WEB ROUTING STRATEGY (Production & Dev)
                                ---------------------------------------
                            */}
                            {!isNative && (
                              <>
                                {isMarketingDomain && PublicRoutes()}
                                {isAppDomain && (
                                  <Route path="/" element={<RootRedirect />} />
                                )}
                                {isAppDomain && AppRoutes()}

                                {!isProduction && PublicRoutes()}
                                {!isProduction && AppRoutes()}

                                {/* Funnel Proxy Routes */}
                                <Route
                                  path="/f/:subdomain"
                                  element={<FunnelProxy />}
                                />
                                <Route
                                  path="/f/:subdomain/:funnelId/:stepId"
                                  element={<FunnelProxy />}
                                />

                                <Route path="*" element={<CatchAllRoute />} />
                              </>
                            )}
                          </Routes>
                        </Suspense>
                      </MaintenanceCheck>
                    </MobileAuthProvider>
                  </ThemeProvider>
                </GenerationProvider>
              </GnomeProvider>
            </BusinessBrainProvider>
          </ProfileProvider>
      </StrategyInsightsProvider>
    </WorkspaceProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
